import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";
import { TAG_COLORS, TAG_COLOR_KEYS, colorClasses } from "../lib/tags";
import { normalizePattern, matches } from "../lib/matchPattern";

const newId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random());

const TABS = [
  { id: "formatting", label: "Formatting" },
  { id: "tags", label: "Tags & mappings" },
  { id: "suppliers", label: "Suppliers" },
  { id: "employees", label: "Employees" },
];

const SettingsModal = ({ settings, prefillPattern, onClose, onSaved }) => {
  const [activeTab, setActiveTab] = useState(prefillPattern ? "tags" : "formatting");
  const [thousands, setThousands] = useState(settings.thousands_separator !== false);
  const [currency, setCurrency] = useState(!!settings.currency_symbol);
  const [tags, setTags] = useState(() =>
    (settings.tags || []).map((t) => ({
      id: t.id || newId(),
      label: t.label,
      color: t.color || "gray",
      kind: t.kind || "supplier",
    }))
  );
  const [mappings, setMappings] = useState(() => {
    const firstTag = (settings.tags && settings.tags[0]?.label) || "";
    const base = (settings.label_mappings || []).map((m) => ({ ...m }));
    if (prefillPattern) base.push({ id: newId(), pattern: prefillPattern, tag: firstTag });
    return base;
  });
  const [suppliers, setSuppliers] = useState(() =>
    (settings.suppliers || []).map((s) => ({ id: s.id || newId(), name: s.name, tag: s.tag }))
  );
  const [employees, setEmployees] = useState(() =>
    (settings.employees || []).map((e) => ({ id: e.id || newId(), name: e.name, tag: e.tag }))
  );
  const [saving, setSaving] = useState(false);
  const [openColorFor, setOpenColorFor] = useState(null);
  const [tagUsage, setTagUsage] = useState({});
  const [tryText, setTryText] = useState("");

  const supplierTags = useMemo(() => tags.filter((t) => (t.kind || "supplier") !== "employee"), [tags]);
  const employeeTags = useMemo(() => tags.filter((t) => (t.kind || "supplier") === "employee"), [tags]);

  const firstMatchIdx = useMemo(() => {
    if (!tryText.trim()) return -1;
    return mappings.findIndex(
      (m) => (m.pattern || "").trim() && m.tag && matches(m.pattern, tryText)
    );
  }, [tryText, mappings]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/tag-usage`);
        if (!cancelled) setTagUsage(res.data || {});
      } catch (e) {
        console.warn("Failed to load tag usage:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateMapping = (idx, patch) =>
    setMappings((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  const removeMapping = (idx) => setMappings((prev) => prev.filter((_, i) => i !== idx));
  const addMapping = () =>
    setMappings((prev) => [...prev, { id: newId(), pattern: "", tag: tags[0]?.label || "" }]);

  const updateTag = (idx, patch) => {
    setTags((prev) => {
      const next = prev.map((t, i) => (i === idx ? { ...t, ...patch } : t));
      // If a tag's kind is switched, drop entries in the opposite list for it.
      if (patch.kind) {
        const label = next[idx].label;
        if (patch.kind === "employee") {
          setSuppliers((s) => s.filter((x) => x.tag !== label));
        } else {
          setEmployees((e) => e.filter((x) => x.tag !== label));
        }
      }
      return next;
    });
  };
  const removeTag = (idx) => {
    const removed = tags[idx];
    const usage = removed?.label ? tagUsage[removed.label] : null;
    const tx = usage?.transactions || 0;
    const mp = usage?.mappings || 0;
    const sp = usage?.suppliers || 0;
    const em = usage?.employees || 0;
    if (tx + mp + sp + em > 0) {
      const parts = [];
      if (tx) parts.push(`${tx} transaction${tx === 1 ? "" : "s"}`);
      if (mp) parts.push(`${mp} mapping${mp === 1 ? "" : "s"}`);
      if (sp) parts.push(`${sp} supplier${sp === 1 ? "" : "s"}`);
      if (em) parts.push(`${em} employee${em === 1 ? "" : "s"}`);
      const ok = window.confirm(
        `"${removed.label}" is used by ${parts.join(", ")}. ` +
          `Removing it will orphan those transactions (they'll show the label without a colour) ` +
          `and will drop the mappings, suppliers and employees pointing at it. Continue?`
      );
      if (!ok) return;
    }
    setTags((prev) => prev.filter((_, i) => i !== idx));
    if (removed?.label) {
      setMappings((prev) => prev.filter((m) => m.tag !== removed.label));
      setSuppliers((prev) => prev.filter((s) => s.tag !== removed.label));
      setEmployees((prev) => prev.filter((e) => e.tag !== removed.label));
    }
  };

  const updateSupplier = (idx, patch) =>
    setSuppliers((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const removeSupplier = (idx) => setSuppliers((prev) => prev.filter((_, i) => i !== idx));

  const updateEmployee = (idx, patch) =>
    setEmployees((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  const removeEmployee = (idx) => setEmployees((prev) => prev.filter((_, i) => i !== idx));

  const ensureKindTag = (kind, defaultBase) => {
    const list = kind === "employee" ? employeeTags : supplierTags;
    if (list.length > 0) return list[0].label;
    const usedColors = new Set(tags.map((t) => t.color));
    const nextColor = TAG_COLOR_KEYS.find((c) => !usedColors.has(c)) || "gray";
    const existing = new Set(tags.map((t) => (t.label || "").toLowerCase()));
    let label = defaultBase;
    let n = 1;
    while (existing.has(label.toLowerCase())) {
      n += 1;
      label = `${defaultBase} ${n}`;
    }
    setTags((prev) => [...prev, { id: newId(), label, color: nextColor, kind }]);
    return label;
  };

  const addSupplierRow = () => {
    const tag = ensureKindTag("supplier", "Supplier");
    setSuppliers((prev) => [...prev, { id: newId(), name: "", tag }]);
  };
  const addEmployeeRow = () => {
    const tag = ensureKindTag("employee", "Employee");
    setEmployees((prev) => [...prev, { id: newId(), name: "", tag }]);
  };

  const addTag = () => {
    const usedColors = new Set(tags.map((t) => t.color));
    const nextColor = TAG_COLOR_KEYS.find((c) => !usedColors.has(c)) || "gray";
    setTags((prev) => [...prev, { id: newId(), label: "", color: nextColor, kind: "supplier" }]);
  };

  const save = async () => {
    const cleaned = tags
      .map((t) => ({ ...t, label: (t.label || "").trim(), kind: t.kind || "supplier" }))
      .filter((t) => t.label);
    const labels = cleaned.map((t) => t.label.toLowerCase());
    if (new Set(labels).size !== labels.length) {
      toast.error("Tag labels must be unique");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, {
        thousands_separator: thousands,
        currency_symbol: currency,
        tags: cleaned,
        suppliers: suppliers
          .filter((s) => s.name.trim() && s.tag && cleaned.some((t) => t.label === s.tag && t.kind !== "employee"))
          .map((s) => ({ ...s, name: s.name.trim() })),
        employees: employees
          .filter((e) => e.name.trim() && e.tag && cleaned.some((t) => t.label === e.tag && t.kind === "employee"))
          .map((e) => ({ ...e, name: e.name.trim() })),
        label_mappings: mappings
          .filter((m) => m.pattern.trim() && m.tag)
          .map((m) => ({ ...m, pattern: m.pattern.trim() })),
      });
      const n = res.data?.auto_labelled || 0;
      toast.success(
        n > 0 ? `Settings saved · ${n} transaction${n === 1 ? "" : "s"} auto-labelled` : "Settings saved"
      );
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      console.error("Failed to save settings:", e);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const renderTagUsage = (label, idx) => {
    const u = tagUsage[label];
    if (!u) return null;
    const parts = [];
    if (u.transactions) parts.push(`${u.transactions} tx`);
    if (u.mappings) parts.push(`${u.mappings} mapping${u.mappings === 1 ? "" : "s"}`);
    if (u.suppliers) parts.push(`${u.suppliers} supplier${u.suppliers === 1 ? "" : "s"}`);
    if (u.employees) parts.push(`${u.employees} employee${u.employees === 1 ? "" : "s"}`);
    if (parts.length === 0) {
      return (
        <span
          className="text-[10px] text-gray-400 whitespace-nowrap"
          data-testid={`tag-usage-${idx}`}
          title="Not used yet"
        >
          unused
        </span>
      );
    }
    return (
      <span
        className="text-[10px] text-gray-500 whitespace-nowrap"
        data-testid={`tag-usage-${idx}`}
        title={parts.join(", ")}
      >
        {parts.join(" · ")}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="settings-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 pt-4 border-b border-gray-200">
          <div className="flex items-end gap-1 -mb-px">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`settings-tab-${t.id}`}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === t.id
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            data-testid="settings-close"
            className="text-gray-400 hover:text-gray-600 text-xl leading-none mb-2"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {activeTab === "formatting" && (
            <section data-testid="settings-panel-formatting">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Number formatting</h3>
              <label className="flex items-center gap-2 text-sm text-gray-700 py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={thousands}
                  data-testid="settings-thousands-toggle"
                  onChange={(e) => setThousands(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                Show thousands separators in numbers (1,234.56)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={currency}
                  data-testid="settings-currency-toggle"
                  onChange={(e) => setCurrency(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                Show &quot;$&quot; signs aligned in a column in transaction tables
              </label>
            </section>
          )}

          {activeTab === "tags" && (
            <div data-testid="settings-panel-tags" className="space-y-6">
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Transaction tags</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Tags you can attach to transactions. Each tag&apos;s <em>kind</em> determines which
                  list the Notes column autocompletes from — <b>supplier</b> pulls from your Suppliers,
                  <b> employee</b> pulls from your Employees.
                </p>
                {tags.length === 0 && (
                  <p className="text-sm text-gray-400 italic mb-2" data-testid="settings-no-tags">
                    No tags yet.
                  </p>
                )}
                <div className="space-y-2">
                  {tags.map((t, idx) => (
                    <div key={t.id} className="flex items-center gap-2" data-testid={`tag-row-${idx}`}>
                      <div className="relative">
                        <button
                          onClick={() => setOpenColorFor(openColorFor === t.id ? null : t.id)}
                          data-testid={`tag-color-${idx}`}
                          className={`h-6 w-6 rounded-full border border-gray-300 ${TAG_COLORS[t.color]?.swatch || TAG_COLORS.gray.swatch}`}
                          title="Change colour"
                        />
                        {openColorFor === t.id && (
                          <div
                            className="absolute z-20 mt-1 p-2 bg-white border border-gray-200 rounded-md shadow-lg flex flex-wrap gap-1.5 w-[172px]"
                            data-testid={`tag-color-picker-${idx}`}
                          >
                            {TAG_COLOR_KEYS.map((c) => (
                              <button
                                key={c}
                                onClick={() => {
                                  updateTag(idx, { color: c });
                                  setOpenColorFor(null);
                                }}
                                data-testid={`tag-color-option-${idx}-${c}`}
                                className={`h-6 w-6 rounded-full border ${t.color === c ? "border-gray-800 ring-2 ring-blue-400" : "border-gray-300"} ${TAG_COLORS[c].swatch}`}
                                title={c}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <input
                        type="text"
                        value={t.label}
                        placeholder="Tag name"
                        data-testid={`tag-label-${idx}`}
                        onChange={(e) => updateTag(idx, { label: e.target.value })}
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <select
                        value={t.kind || "supplier"}
                        data-testid={`tag-kind-${idx}`}
                        onChange={(e) => updateTag(idx, { kind: e.target.value })}
                        title="What kind of entities does this tag represent?"
                        className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="supplier">supplier</option>
                        <option value="employee">employee</option>
                      </select>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${colorClasses(t.color)}`}
                      >
                        {t.label || "Preview"}
                      </span>
                      {renderTagUsage(t.label, idx)}
                      <button
                        onClick={() => removeTag(idx)}
                        data-testid={`tag-remove-${idx}`}
                        className="text-gray-300 hover:text-red-500 text-lg font-bold leading-none flex-shrink-0"
                        title="Remove tag"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addTag}
                  data-testid="settings-add-tag"
                  className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50"
                >
                  + Add tag
                </button>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Transaction label mappings</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Transactions whose description matches a pattern are automatically tagged. Matching is
                  case-insensitive and any word that contains a digit is treated as a wildcard — so
                  &quot;ALECTRA UT J2U8R7&quot; also matches &quot;ALECTRA UT J2U806&quot;. Auto-applied
                  tags appear dashed until you confirm them with ✓.
                </p>
                {mappings.length === 0 && (
                  <p className="text-sm text-gray-400 italic mb-2" data-testid="settings-no-mappings">
                    No mappings yet.
                  </p>
                )}
                <div className="space-y-2">
                  {mappings.map((m, idx) => {
                    const isMatch = tryText.trim() && (m.pattern || "").trim() && matches(m.pattern, tryText);
                    const isFirstMatch = idx === firstMatchIdx;
                    const normalized = (m.pattern || "").trim() ? normalizePattern(m.pattern) : "";
                    return (
                      <div key={m.id || idx} className="flex items-center gap-2" data-testid={`mapping-row-${idx}`}>
                        <input
                          type="text"
                          value={m.pattern}
                          placeholder='e.g. "FD82308470012 MSP"'
                          data-testid={`mapping-pattern-${idx}`}
                          title={normalized ? `Normalized: "${normalized}"` : undefined}
                          onChange={(e) => updateMapping(idx, { pattern: e.target.value })}
                          className="flex-1 min-w-0 border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span className="text-xs text-gray-400 flex-shrink-0">is</span>
                        <select
                          value={m.tag}
                          data-testid={`mapping-tag-${idx}`}
                          onChange={(e) => updateMapping(idx, { tag: e.target.value })}
                          className={`border rounded-full px-2.5 py-1 text-xs font-medium cursor-pointer focus:outline-none ${colorClasses(tags.find((t) => t.label === m.tag)?.color)}`}
                        >
                          {tags.length === 0 && <option value="">No tags — add one first</option>}
                          {tags.map((t) => (
                            <option key={t.id} value={t.label}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                        {tryText.trim() && (
                          isMatch ? (
                            <span
                              data-testid={`mapping-match-${idx}`}
                              title={isFirstMatch ? "Matches — this tag would be applied (first match wins)" : "Matches — but earlier row would apply first"}
                              className={`text-xs font-medium whitespace-nowrap ${isFirstMatch ? "text-green-600" : "text-gray-400"}`}
                            >
                              {isFirstMatch ? "✓ match" : "✓"}
                            </span>
                          ) : (
                            <span
                              data-testid={`mapping-nomatch-${idx}`}
                              title="Does not match"
                              className="text-xs font-medium text-gray-300 whitespace-nowrap"
                            >
                              ✗
                            </span>
                          )
                        )}
                        <button
                          onClick={() => removeMapping(idx)}
                          data-testid={`mapping-remove-${idx}`}
                          className="text-gray-300 hover:text-red-500 text-lg font-bold leading-none flex-shrink-0"
                          title="Remove mapping"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={addMapping}
                  disabled={tags.length === 0}
                  data-testid="settings-add-mapping"
                  className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  + Add mapping
                </button>

                {mappings.length > 0 && (
                  <div className="mt-4 border border-dashed border-gray-300 rounded-md p-3 bg-gray-50" data-testid="mapping-tryit">
                    <label className="text-xs font-medium text-gray-600 block mb-1.5">
                      Try a description
                      <span className="text-gray-400 font-normal"> — paste any transaction description to preview which mapping (if any) would tag it.</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={tryText}
                        onChange={(e) => setTryText(e.target.value)}
                        placeholder='e.g. "ALECTRA UT J2U806"'
                        data-testid="mapping-tryit-input"
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      />
                      {tryText.trim() ? (
                        firstMatchIdx >= 0 ? (
                          <span
                            data-testid="mapping-tryit-result"
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses(tags.find((t) => t.label === mappings[firstMatchIdx].tag)?.color)}`}
                            title={`Would be tagged as "${mappings[firstMatchIdx].tag}" by row ${firstMatchIdx + 1}`}
                          >
                            ✓ {mappings[firstMatchIdx].tag}
                          </span>
                        ) : (
                          <span
                            data-testid="mapping-tryit-result"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 whitespace-nowrap"
                            title="No mapping matches this description"
                          >
                            ✗ no match
                          </span>
                        )
                      ) : (
                        <span className="text-xs text-gray-400 whitespace-nowrap">—</span>
                      )}
                    </div>
                    {tryText.trim() && (
                      <p className="mt-1.5 text-[10px] text-gray-400 font-mono" data-testid="mapping-tryit-normalized">
                        Normalized: &quot;{normalizePattern(tryText)}&quot;
                      </p>
                    )}
                  </div>
                )}
              </section>
            </div>
          )}

          {activeTab === "suppliers" && (
            <section data-testid="settings-panel-suppliers">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Suppliers</h3>
              <p className="text-xs text-gray-500 mb-3">
                Suppliers belong to a <b>supplier-kind</b> tag (e.g. &quot;Uline&quot; for a Packaging tag).
                When a transaction carries that tag, its Notes field suggests these suppliers. The
                Overview groups spend by tag and supplier.
              </p>
              {supplierTags.length === 0 && suppliers.length === 0 && (
                <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2 mb-3">
                  You don&apos;t have a supplier-kind tag yet — clicking <b>Add supplier</b> will create
                  a default &quot;Supplier&quot; tag you can rename on the Tags tab.
                </p>
              )}
              {suppliers.length === 0 && (
                <p className="text-sm text-gray-400 italic mb-2" data-testid="settings-no-suppliers">
                  No suppliers yet.
                </p>
              )}
              <div className="space-y-2">
                {suppliers.map((s, idx) => (
                  <div key={s.id} className="flex items-center gap-2" data-testid={`supplier-row-${idx}`}>
                    <input
                      type="text"
                      value={s.name}
                      placeholder="Supplier name"
                      data-testid={`supplier-name-${idx}`}
                      onChange={(e) => updateSupplier(idx, { name: e.target.value })}
                      className="flex-1 min-w-0 border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-xs text-gray-400 flex-shrink-0">for</span>
                    <select
                      value={s.tag}
                      data-testid={`supplier-tag-${idx}`}
                      onChange={(e) => updateSupplier(idx, { tag: e.target.value })}
                      className={`border rounded-full px-2.5 py-1 text-xs font-medium cursor-pointer focus:outline-none ${colorClasses(tags.find((t) => t.label === s.tag)?.color)}`}
                    >
                      {supplierTags.length === 0 && <option value="">No supplier tags</option>}
                      {supplierTags.map((t) => (
                        <option key={t.id} value={t.label}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeSupplier(idx)}
                      data-testid={`supplier-remove-${idx}`}
                      className="text-gray-300 hover:text-red-500 text-lg font-bold leading-none flex-shrink-0"
                      title="Remove supplier"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={addSupplierRow}
                data-testid="settings-add-supplier"
                className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50"
              >
                + Add supplier
              </button>
            </section>
          )}

          {activeTab === "employees" && (
            <section data-testid="settings-panel-employees">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Employees</h3>
              <p className="text-xs text-gray-500 mb-3">
                Employees belong to an <b>employee-kind</b> tag (e.g. &quot;Alice Smith&quot; for an
                &quot;Employee Payment&quot; tag). When a transaction carries that tag, its Notes field
                suggests these employees.
              </p>
              {employeeTags.length === 0 && employees.length === 0 && (
                <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2 mb-3">
                  You don&apos;t have an employee-kind tag yet — clicking <b>Add employee</b> will
                  create a default &quot;Employee&quot; tag you can rename on the Tags tab.
                </p>
              )}
              {employees.length === 0 && (
                <p className="text-sm text-gray-400 italic mb-2" data-testid="settings-no-employees">
                  No employees yet.
                </p>
              )}
              <div className="space-y-2">
                {employees.map((e, idx) => (
                  <div key={e.id} className="flex items-center gap-2" data-testid={`employee-row-${idx}`}>
                    <input
                      type="text"
                      value={e.name}
                      placeholder="Employee name"
                      data-testid={`employee-name-${idx}`}
                      onChange={(ev) => updateEmployee(idx, { name: ev.target.value })}
                      className="flex-1 min-w-0 border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-xs text-gray-400 flex-shrink-0">for</span>
                    <select
                      value={e.tag}
                      data-testid={`employee-tag-${idx}`}
                      onChange={(ev) => updateEmployee(idx, { tag: ev.target.value })}
                      className={`border rounded-full px-2.5 py-1 text-xs font-medium cursor-pointer focus:outline-none ${colorClasses(tags.find((t) => t.label === e.tag)?.color)}`}
                    >
                      {employeeTags.length === 0 && <option value="">No employee tags</option>}
                      {employeeTags.map((t) => (
                        <option key={t.id} value={t.label}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeEmployee(idx)}
                      data-testid={`employee-remove-${idx}`}
                      className="text-gray-300 hover:text-red-500 text-lg font-bold leading-none flex-shrink-0"
                      title="Remove employee"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={addEmployeeRow}
                data-testid="settings-add-employee"
                className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50"
              >
                + Add employee
              </button>
            </section>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            data-testid="settings-cancel"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
